/**
 * Finery London — Mobile PDP Image Zoom Lightbox
 * Vanilla JS | No dependencies
 *
 * Features:
 *  - Full-screen overlay/lightbox on tap of any .carousel-slide (image container)
 *  - High-res image loading (width: 2048)
 *  - Smooth pinch-to-zoom (multi-touch)
 *  - Swipe left/right to navigate between product images
 *  - Keyboard support (Escape to close, ArrowLeft/ArrowRight)
 *  - Background scroll lock when open
 *  - Hardware-accelerated CSS transforms
 */

(function () {
  'use strict';

  /* ─── Constants ─────────────────────────────────────────────────── */
  const SWIPE_THRESHOLD = 50;      // px: min horizontal travel to count as a swipe
  const SWIPE_VELOCITY = 0.3;      // px/ms: min speed to trigger swipe
  const DOUBLE_TAP_DELAY = 250;    // ms: window for double-tap detection
  const ZOOM_IN_SCALE = 2.5;       // scale applied by double-tap zoom
  const MIN_SCALE = 1;
  const MAX_SCALE = 5;

  /* ─── State ──────────────────────────────────────────────────────── */
  let gallery = [];          // [{ src, alt }] — all zoomable slides
  let currentIndex = 0;      // currently visible slide index
  let isOpen = false;

  // Pan / pinch state
  let scale = 1;
  let panX = 0;
  let panY = 0;
  let lastScale = 1;
  let lastPanX = 0;
  let lastPanY = 0;
  let originX = 0;
  let originY = 0;

  // Touch state
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartTime = 0;
  let lastTapTime = 0;
  let initialPinchDistance = 0;
  let isPinching = false;

  /* ─── DOM references (created once) ─────────────────────────────── */
  let overlay, track, prevBtn, nextBtn, closeBtn, counter, spinner;

  /* ─── Image preload cache ────────────────────────────────────────── */
  // Keyed by hiRes URL → HTMLImageElement (already fetched / decoded)
  const preloadCache = new Map();

  /* ─── Build lightbox DOM ─────────────────────────────────────────── */
  function buildLightbox() {
    if (document.getElementById('fl-zoom-overlay')) return;

    overlay = document.createElement('div');
    overlay.id = 'fl-zoom-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Image zoom viewer');
    overlay.setAttribute('tabindex', '-1');

    overlay.innerHTML = `
      <button class="fl-zoom-close" id="fl-zoom-close" aria-label="Close zoom view">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             width="24" height="24">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
      <div class="fl-zoom-track-wrapper">
        <div class="fl-zoom-track" id="fl-zoom-track"></div>
      </div>
      <button class="fl-zoom-nav fl-zoom-prev" id="fl-zoom-prev" aria-label="Previous image">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
             width="20" height="20">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <button class="fl-zoom-nav fl-zoom-next" id="fl-zoom-next" aria-label="Next image">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"
             width="20" height="20">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
      <div class="fl-zoom-counter" id="fl-zoom-counter" aria-live="polite"></div>
      <div class="fl-zoom-spinner" id="fl-zoom-spinner" aria-hidden="true">
        <div class="fl-zoom-spinner-ring"></div>
      </div>
    `;

    document.body.appendChild(overlay);

    track = overlay.querySelector('#fl-zoom-track');
    closeBtn = overlay.querySelector('#fl-zoom-close');
    prevBtn = overlay.querySelector('#fl-zoom-prev');
    nextBtn = overlay.querySelector('#fl-zoom-next');
    counter = overlay.querySelector('#fl-zoom-counter');
    spinner = overlay.querySelector('#fl-zoom-spinner');

    /* ── Event listeners ── */
    closeBtn.addEventListener('click', closeLightbox);
    prevBtn.addEventListener('click', () => navigate(-1));
    nextBtn.addEventListener('click', () => navigate(1));

    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
    });

    // Tap on backdrop (overlay itself, not image) closes
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeLightbox();
    });

    /* ── Touch events on the track (image area) ── */
    track.addEventListener('touchstart', onTouchStart, { passive: true });
    track.addEventListener('touchmove', onTouchMove, { passive: false });
    track.addEventListener('touchend', onTouchEnd, { passive: true });

    /* ── Mouse wheel zoom (desktop fallback) ── */
    track.addEventListener('wheel', onWheel, { passive: false });
  }

  /* ─── Open lightbox at given index ─────────────────────────────── */
  function openLightbox(index) {
    if (!gallery.length) return;
    buildLightbox();

    currentIndex = Math.max(0, Math.min(index, gallery.length - 1));
    isOpen = true;

    hideThirdPartyWidgets();  // uygulama widget'larını gizle
    document.body.classList.add('fl-zoom-active');
    overlay.setAttribute('open', '');
    overlay.classList.add('fl-zoom-is-open');
    overlay.focus();

    renderSlide(currentIndex);
    updateNav();
    // Kick off background preload of neighbours immediately on open
    preloadAdjacent(currentIndex);
  }

  /* ─── Close lightbox ────────────────────────────────────────── */
  function closeLightbox() {
    if (!isOpen) return;
    isOpen = false;

    overlay.removeAttribute('open');
    overlay.classList.remove('fl-zoom-is-open');
    document.body.classList.remove('fl-zoom-active');
    restoreThirdPartyWidgets(); // widget'ları geri getir

    setTimeout(() => {
      track.innerHTML = '';
      resetTransform();
    }, 300);
  }

  /* ─── Third-party widget gizleme / gösterme ──────────────────────── */
  // Shopify app widget'ları (chat, rewards, vb.) position:fixed ile
  // renderı çok yüksek z-index kullanır ve overlay'in üstlerine çıkar.
  // Modal açılırken gizliyor, kapanırken döndürüyoruz.
  const WIDGET_KEY = 'data-fl-vis'; // orijinal visibility değerini saklamak için

  function hideThirdPartyWidgets() {
    // body'nin doğrudan çocukları (en yaygın widget konumu)
    document.querySelectorAll('body > *').forEach((el) => {
      if (el.id === 'fl-zoom-overlay') return;
      const pos = window.getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') return;
      el.setAttribute(WIDGET_KEY, el.style.visibility || '');
      el.style.setProperty('visibility', 'hidden', 'important');
    });

    // Portala yerleştirilmiş iframe / div widget'ları
    document.querySelectorAll(
      'iframe[style*="fixed"], div[style*="position: fixed"], div[style*="position:fixed"]'
    ).forEach((el) => {
      if (el.closest('#fl-zoom-overlay') || el.hasAttribute(WIDGET_KEY)) return;
      el.setAttribute(WIDGET_KEY, el.style.visibility || '');
      el.style.setProperty('visibility', 'hidden', 'important');
    });
  }

  function restoreThirdPartyWidgets() {
    document.querySelectorAll(`[${WIDGET_KEY}]`).forEach((el) => {
      const original = el.getAttribute(WIDGET_KEY);
      if (original) {
        el.style.visibility = original;
      } else {
        el.style.removeProperty('visibility');
      }
      el.removeAttribute(WIDGET_KEY);
    });
  }

  /* ─── Navigate between slides ─────────────────────────────────────── */
  let isNavigating = false; // geçiş sırasında çift tetiklemeyi önler

  function navigate(dir) {
    const next = currentIndex + dir;
    if (next < 0 || next >= gallery.length) return;
    if (isNavigating) return;

    isNavigating = true;
    const DURATION = 320; // ms — iOS native gallery hissi
    const easing   = 'cubic-bezier(0.4, 0, 0.2, 1)';

    // 1) Mevcut görseli bul
    const oldImg = track.querySelector('.fl-zoom-img');

    // 2) Yeni görseli hazırla (cache'den veya network'ten)
    currentIndex = next;
    resetTransform();

    const slide = gallery[currentIndex];
    const url   = slide.hiRes || slide.src;

    const newImg = document.createElement('img');
    newImg.className  = 'fl-zoom-img';
    newImg.alt        = slide.alt || '';
    newImg.draggable  = false;
    newImg.src        = url; // cache hit → anında, yoksa network

    // 3) Yeni görsel ekranda görünmeden önce offset konuma yerleştir
    //    dir > 0 → sağdan geliyor, dir < 0 → soldan geliyor
    const fromX = dir > 0 ? '100%' : '-100%';
    const toX   = dir > 0 ? '-100%' : '100%';
    newImg.style.transform = `translateX(${fromX})`;
    track.appendChild(newImg);

    // Cache'e kaydet
    newImg.addEventListener('load', () => preloadCache.set(url, newImg), { once: true });

    // 4) Bir sonraki frame'de her iki görsele transition uygula
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const t = `transform ${DURATION}ms ${easing}`;

        // Yeni görsel sağdan/soldan merkeze kayar
        newImg.style.transition = t;
        newImg.style.transform  = 'translateX(0)';

        // Eski görsel zıt yöne kayar
        if (oldImg) {
          oldImg.style.transition = t;
          oldImg.style.transform  = `translateX(${toX})`;
        }

        // 5) Geçiş biter bitmez eski görseli kaldır
        setTimeout(() => {
          if (oldImg && oldImg.parentNode) oldImg.remove();
          newImg.style.transition = '';
          isNavigating = false;
          updateNav();
          counter.textContent = `${currentIndex + 1} / ${gallery.length}`;
          requestAnimationFrame(() => preloadAdjacent(currentIndex));
        }, DURATION + 20);
      });
    });
  }

  /* ─── Render slide image (ilk açılış için) ─────────────────────────── */
  function renderSlide(index) {
    const slide = gallery[index];
    if (!slide) return;

    track.innerHTML = '';
    resetTransform();

    const url    = slide.hiRes || slide.src;
    const cached = preloadCache.get(url);

    if (cached && cached.complete && cached.naturalWidth > 0) {
      // ✨ Cache hit — anında yükle
      const img = document.createElement('img');
      img.className = 'fl-zoom-img fl-first-in';
      img.alt       = slide.alt || '';
      img.draggable = false;
      img.src       = url;
      track.appendChild(img);
      requestAnimationFrame(() => setTimeout(() => img.classList.remove('fl-first-in'), 50));
      spinner.classList.remove('fl-zoom-spinner--visible');
    } else {
      // ⏳ Henüz yüklenmedi — spinner göster
      spinner.classList.add('fl-zoom-spinner--visible');

      const img = document.createElement('img');
      img.className = 'fl-zoom-img fl-first-in';
      img.alt       = slide.alt || '';
      img.draggable = false;

      img.addEventListener('load', () => {
        spinner.classList.remove('fl-zoom-spinner--visible');
        preloadCache.set(url, img);
        setTimeout(() => img.classList.remove('fl-first-in'), 50);
      });

      img.addEventListener('error', () => {
        spinner.classList.remove('fl-zoom-spinner--visible');
      });

      track.appendChild(img);
      img.src = url;
    }

    counter.textContent = `${index + 1} / ${gallery.length}`;
    requestAnimationFrame(() => preloadAdjacent(index));
  }

  /* ─── Preload adjacent slides into the cache ────────────────────── */
  function preloadAdjacent(index) {
    [-1, 1].forEach((offset) => {
      const i = index + offset;
      if (i < 0 || i >= gallery.length) return;

      const slide = gallery[i];
      const url = slide.hiRes || slide.src;

      if (preloadCache.has(url)) return;  // already cached

      const img = new Image();
      img.onload = () => preloadCache.set(url, img);
      // Use low-priority fetch hint so it doesn't compete with the
      // currently-visible image's own load
      img.fetchPriority = 'low';
      img.src = url;
      // Optimistically add to cache immediately so we don't double-fetch
      preloadCache.set(url, img);
    });
  }

  /* ─── Navigation button visibility ─────────────────────────────── */
  function updateNav() {
    prevBtn.style.opacity = currentIndex === 0 ? '0.3' : '1';
    prevBtn.style.pointerEvents = currentIndex === 0 ? 'none' : '';
    nextBtn.style.opacity = currentIndex === gallery.length - 1 ? '0.3' : '1';
    nextBtn.style.pointerEvents = currentIndex === gallery.length - 1 ? 'none' : '';
  }

  /* ─── Transform helpers ─────────────────────────────────────────── */
  function resetTransform() {
    scale = 1;
    panX = 0;
    panY = 0;
    lastScale = 1;
    lastPanX = 0;
    lastPanY = 0;
    applyTransform();
  }

  function applyTransform() {
    const img = track.querySelector('.fl-zoom-img');
    if (!img) return;
    img.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
  }

  function clampPan() {
    if (scale <= 1) {
      panX = 0;
      panY = 0;
      return;
    }
    // Image is 100vw × 100dvh at scale=1, so overflow is purely from zoom.
    const overflowX = (window.innerWidth  * (scale - 1)) / 2;
    const overflowY = (window.innerHeight * (scale - 1)) / 2;
    panX = Math.max(-overflowX, Math.min(overflowX, panX));
    panY = Math.max(-overflowY, Math.min(overflowY, panY));
  }

  /* ─── Touch event handlers ──────────────────────────────────────── */
  function getDistance(t1, t2) {
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function getMidpoint(t1, t2) {
    return {
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2,
    };
  }

  function onTouchStart(e) {
    if (e.touches.length === 2) {
      // Pinch start
      isPinching = true;
      initialPinchDistance = getDistance(e.touches[0], e.touches[1]);
      lastScale = scale;
      const mid = getMidpoint(e.touches[0], e.touches[1]);
      const rect = track.getBoundingClientRect();
      originX = mid.x - rect.left - rect.width / 2;
      originY = mid.y - rect.top - rect.height / 2;
    } else if (e.touches.length === 1) {
      isPinching = false;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchStartTime = Date.now();
      lastPanX = panX;
      lastPanY = panY;
    }
  }

  function onTouchMove(e) {
    if (e.touches.length === 2 && isPinching) {
      // Pinch-to-zoom — always take full control
      e.preventDefault();
      const currentDist = getDistance(e.touches[0], e.touches[1]);
      const rawScale = lastScale * (currentDist / initialPinchDistance);
      scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, rawScale));
      clampPan();
      applyTransform();
    } else if (e.touches.length === 1 && !isPinching) {
      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;

      if (scale > 1) {
        // Zoomed in: take full control of both axes for panning.
        // Must preventDefault here even for vertical because we're
        // panning the transform, not the scroll container.
        e.preventDefault();
        panX = lastPanX + dx;
        panY = lastPanY + dy;
        clampPan();
        applyTransform();
      } else {
        // Not zoomed: block horizontal movement so the page doesn't
        // scroll sideways during a left/right swipe-to-navigate gesture.
        // Vertical scrolling (touch-action: pan-y on the img) is handled
        // natively by the browser — no need to call preventDefault for it.
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
          e.preventDefault();
        }
      }
    }
  }

  function onTouchEnd(e) {
    if (isPinching) {
      isPinching = false;
      if (scale < 1.05) resetTransform();
      return;
    }

    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartX;
    const dy = touch.clientY - touchStartY;
    const dt = Date.now() - touchStartTime;
    const velocity = Math.abs(dx) / dt;
    const now = Date.now();

    // Double-tap to zoom
    if (now - lastTapTime < DOUBLE_TAP_DELAY && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      lastTapTime = 0;
      const img = track.querySelector('.fl-zoom-img');

      // Briefly add a smooth transition class for the snap animation only
      if (img) {
        img.style.transition = 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
        setTimeout(() => { if (img) img.style.transition = ''; }, 280);
      }

      if (scale > 1) {
        // Zoom out
        scale = 1;
        panX = 0;
        panY = 0;
      } else {
        // Zoom in to tap point
        scale = ZOOM_IN_SCALE;
        const rect = track.getBoundingClientRect();
        panX = (rect.width / 2 - (touch.clientX - rect.left)) * (ZOOM_IN_SCALE - 1) / ZOOM_IN_SCALE;
        panY = (rect.height / 2 - (touch.clientY - rect.top)) * (ZOOM_IN_SCALE - 1) / ZOOM_IN_SCALE;
        clampPan();
      }
      applyTransform();
      return;
    }
    lastTapTime = now;

    // Swipe navigation (only when not zoomed)
    if (scale <= 1 && Math.abs(dx) > SWIPE_THRESHOLD && velocity > SWIPE_VELOCITY) {
      navigate(dx < 0 ? 1 : -1);
    }
  }

  function onWheel(e) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.85 : 1.15;
    scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * delta));
    if (scale <= 1) { panX = 0; panY = 0; }
    clampPan();
    applyTransform();
  }

  /* ─── Collect images from the page ─────────────────────────────── */
  function collectGallery() {
    gallery = [];

    // Query slides that have a zoom source attached by Liquid
    const slides = document.querySelectorAll(
      '.product__media-list [data-zoom-src], .fl-zoom-source'
    );

    slides.forEach((el) => {
      const src = el.dataset.zoomSrc || el.querySelector('img')?.src || '';
      const hiRes = el.dataset.zoomHires || src;
      const alt = el.dataset.zoomAlt || el.querySelector('img')?.alt || '';
      if (src) gallery.push({ src, hiRes, alt });
    });

    // Fallback: collect all visible product images if no data-zoom-src found
    if (!gallery.length) {
      const imgs = document.querySelectorAll(
        '.product__media-list .product__media img, ' +
        '.product-media-container img'
      );
      imgs.forEach((img) => {
        if (!img.src) return;
        // Try to derive a 2048-wide URL from the Shopify CDN src
        const hiRes = shopifyHighResUrl(img.src, 2048);
        gallery.push({ src: img.src, hiRes, alt: img.alt || '' });
      });
    }
  }

  /**
   * Derive a Shopify image URL at a given width.
   * Shopify CDN format: …/files/image_{size}.jpg?v=…
   */
  function shopifyHighResUrl(src, width) {
    if (!src) return src;
    // Strip existing size param from Shopify CDN urls
    return src.replace(/(_\d+x\d*|_\d*x\d+)(\.jpg|\.png|\.webp|\.gif)/i, `_${width}x$2`)
              .replace(/width=\d+/, `width=${width}`);
  }

  /* ─── Wire up gallery item click → open ─────────────────────────── */
  function wireClickHandlers() {
    // Intercept clicks on the product media toggle (the native Dawn opener)
    // or directly on .fl-zoom-trigger containers we add in Liquid
    document.addEventListener('click', (e) => {
      // Only act on mobile (max 749px) - matches Dawn's breakpoint
      if (window.innerWidth >= 750) return;

      const trigger = e.target.closest(
        '.product__media-toggle, .product__modal-opener--image, .fl-zoom-trigger'
      );
      if (!trigger) return;

      // Find what media item this belongs to
      const mediaContainer = trigger.closest('[data-zoom-src], .product-media-container');
      if (!mediaContainer) return;

      // Prevent the native Dawn modal from opening
      e.stopImmediatePropagation();
      e.preventDefault();

      collectGallery();
      if (!gallery.length) return;

      // Determine which index to open at
      let idx = 0;
      const zoomSrc = mediaContainer.dataset.zoomSrc;
      if (zoomSrc) {
        idx = gallery.findIndex((g) => g.src === zoomSrc || g.hiRes === zoomSrc);
        if (idx < 0) idx = 0;
      } else {
        // Match by the image src
        const img = mediaContainer.querySelector('img');
        if (img) {
          idx = gallery.findIndex((g) => g.src === img.src);
          if (idx < 0) idx = 0;
        }
      }

      openLightbox(idx);
    }, true /* capture — so we beat the ModalOpener listener */);
  }

  /* ─── Init ───────────────────────────────────────────────────────── */
  function init() {
    buildLightbox();
    wireClickHandlers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for Shopify theme editor live reloads
  window.FLZoom = { open: openLightbox, close: closeLightbox, collect: collectGallery };
})();
